import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CrownSVG, ShieldSVG, BrokenHeartSVG, StarSVG } from './GameIcons';

const GAMES = [
  {
    id: 'the-last-kingdom',
    title: 'The Last Kingdom',
    subtitle: 'Gioco di carte',
    description: 'Sfida gli avversari e conquista i Regni in un gioco avvincente di strategia. Sopravvivi con almeno una corona mentre gli altri finiscono in prigione.',
    SvgIcon: CrownSVG,
    route: '/kingdom',
    available: true,
    players: '4 giocatori',
    // Warm gold — medieval
    bg: 'linear-gradient(145deg, #1a1000 0%, #2d1d00 50%, #1a1000 100%)',
    bgLight: 'rgba(251,191,36,0.08)',
    border: 'rgba(251,191,36,0.5)',
    borderLight: 'rgba(251,191,36,0.15)',
    glow: 'rgba(251,191,36,0.18)',
    accent: '#FBBF24',
    accentLight: '#FDE68A',
    tag: 'DISPONIBILE',
    tagColor: '#22c55e',
    tagBg: 'rgba(34,197,94,0.12)',
    btnBg: 'linear-gradient(135deg, #FBBF24, #D97706)',
    btnShadow: 'rgba(251,191,36,0.4)',
  },
  {
    id: 'game-2',
    title: 'Ten or Nothing',
    subtitle: 'Gioco di carte e strategia',
    description: 'Mantieni i tuoi punti tra 0 e 10. Supera i limiti e perdi una vita. Rimani l ultimo in piedi per vincere.',
    SvgIcon: ShieldSVG,
    route: '/ton/lobby',
    available: true,
    players: '—',
    // Cool blue — tactical
    bg: 'linear-gradient(145deg, #030818 0%, #060f28 50%, #030818 100%)',
    bgLight: 'rgba(59,130,246,0.08)',
    border: 'rgba(59,130,246,0.35)',
    borderLight: 'rgba(59,130,246,0.1)',
    glow: 'rgba(59,130,246,0.12)',
    accent: '#3B82F6',
    accentLight: '#93C5FD',
    tag: 'DISPONIBILE',
    tagColor: '#22c55e',
    tagBg: 'rgba(34,197,94,0.12)',
    btnBg: 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
    btnShadow: 'rgba(59,130,246,0.4)',
  },
  {
    id: 'game-3',
    title: 'Broken Hearts',
    subtitle: 'Gioco di carte',
    description: 'Sopravvivi con almeno un cuore. Carte speciali e colpi di scena in un gioco di strategia e fortuna.',
    SvgIcon: BrokenHeartSVG,
    route: '/bh/lobby',
    available: true,
    players: '4',
    // Pink/rose — broken hearts theme
    bg: 'linear-gradient(145deg, #1a0510 0%, #2d0820 50%, #1a0510 100%)',
    bgLight: 'rgba(236,72,153,0.08)',
    border: 'rgba(236,72,153,0.35)',
    borderLight: 'rgba(236,72,153,0.1)',
    glow: 'rgba(236,72,153,0.12)',
    accent: '#ec4899',
    accentLight: '#f9a8d4',
    tag: 'DISPONIBILE',
    tagColor: '#ec4899',
    tagBg: 'rgba(236,72,153,0.12)',
    btnBg: 'linear-gradient(135deg,#be185d,#9d174d)',
    btnShadow: 'rgba(190,24,93,0.4)',
  },
  {
    id: 'game-4',
    title: 'Crazy Cards',
    subtitle: 'Mini-giochi esplosivi',
    description: 'Parti con 500€ e usa le carte a tuo vantaggio. Moneta, bersaglio, caduta libera e mega ruota ti aspettano!',
    SvgIcon: StarSVG,
    route: '/cc/lobby',
    available: true,
    players: '4',
    bg: 'linear-gradient(145deg, #0a0118 0%, #130228 50%, #0a0118 100%)',
    bgLight: 'rgba(168,85,247,0.08)',
    border: 'rgba(168,85,247,0.35)',
    borderLight: 'rgba(168,85,247,0.1)',
    glow: 'rgba(168,85,247,0.12)',
    accent: '#A855F7',
    accentLight: '#D8B4FE',
    tag: 'DISPONIBILE',
    tagColor: '#22c55e',
    tagBg: 'rgba(34,197,94,0.12)',
    btnBg: 'linear-gradient(135deg,#7c3aed,#5b21b6)',
    btnShadow: 'rgba(124,58,237,0.4)',
  },
];

const GameCard = ({ game, index }) => {
  const navigate = useNavigate();
  const { SvgIcon } = game;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onClick={() => game.available && game.route && navigate(game.route)}
      whileHover={game.available ? { scale: 1.02, y: -4 } : { scale: 1.01 }}
      whileTap={game.available ? { scale: 0.98 } : {}}
      style={{
        cursor: game.available ? 'pointer' : 'default',
        borderRadius: 20,
        border: `1px solid ${game.border}`,
        background: game.bg,
        boxShadow: game.available
          ? `0 0 40px ${game.glow}, 0 8px 32px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)`
          : `0 4px 20px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.03)`,
        padding: '28px 26px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top gloss */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:'40%', background:'linear-gradient(to bottom,rgba(255,255,255,0.05),transparent)', borderRadius:'20px 20px 0 0', pointerEvents:'none' }} />

      {/* Accent glow top-left */}
      <div style={{ position:'absolute', top:0, left:0, width:'60%', height:'50%', background:`radial-gradient(ellipse at 20% 0%, ${game.bgLight} 0%, transparent 70%)`, pointerEvents:'none' }} />

      {/* Tag */}
      <div style={{
        position: 'absolute', top: 16, right: 16,
        background: game.tagBg,
        border: `1px solid ${game.accent}44`,
        borderRadius: 6, padding: '3px 10px',
        fontSize: 9, fontWeight: 800, letterSpacing: '0.12em',
        color: game.tagColor, textTransform: 'uppercase',
      }}>
        {game.tag}
      </div>

      {/* Icon + title row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        {/* Icon container */}
        <div style={{
          width: 64, height: 64, borderRadius: 16, flexShrink: 0,
          background: `rgba(0,0,0,0.3)`,
          border: `1px solid ${game.borderLight}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `inset 0 0 20px ${game.glow}`,
        }}>
          {game.available ? (
            <motion.div
              animate={{ filter: [`drop-shadow(0 0 4px ${game.accent}66)`, `drop-shadow(0 0 12px ${game.accent}cc)`, `drop-shadow(0 0 4px ${game.accent}66)`] }}
              transition={{ duration: 2.5, repeat: Infinity }}
            >
              <SvgIcon />
            </motion.div>
          ) : (
            <div style={{ opacity: 0.35, filter: 'grayscale(0.6)' }}>
              <SvgIcon />
            </div>
          )}
        </div>

        <div style={{ flex: 1 }}>
          <h2 style={{
            margin: '0 0 4px',
            fontFamily: "'Cinzel', serif",
            fontSize: 17, fontWeight: 700,
            color: game.available ? game.accentLight : '#4b5563',
          }}>
            {game.title}
          </h2>
          <p style={{
            margin: 0, fontSize: 11,
            color: game.available ? `${game.accent}99` : '#374151',
            fontStyle: 'italic', fontWeight: 500,
          }}>
            {game.subtitle}
          </p>
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: `linear-gradient(to right, transparent, ${game.border}, transparent)`, marginBottom: 14 }} />

      {/* Description */}
      <p style={{
        margin: '0 0 20px', fontSize: 13, lineHeight: 1.65,
        color: game.available ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)',
      }}>
        {game.description}
      </p>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: game.available ? game.accent : '#374151' }} />
          <span style={{ fontSize: 11, color: game.available ? `${game.accent}88` : '#374151', fontWeight: 600 }}>
            {game.players}
          </span>
        </div>

        {game.available ? (
          <motion.button
            onClick={() => navigate(game.route)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            animate={{ boxShadow: [`0 0 8px ${game.btnShadow}`, `0 0 20px ${game.btnShadow}`, `0 0 8px ${game.btnShadow}`] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              background: game.btnBg,
              border: 'none', borderRadius: 10,
              padding: '8px 22px',
              fontSize: 12, fontWeight: 900,
              color: '#1c1917',
              letterSpacing: '0.06em', textTransform: 'uppercase',
              cursor: 'pointer', fontFamily: "'Cinzel', serif",
            }}
          >
            GIOCA
          </motion.button>
        ) : (
          <div style={{
            border: `1px solid ${game.accent}22`,
            borderRadius: 10, padding: '7px 16px',
            fontSize: 11, fontWeight: 700,
            color: `${game.accent}44`,
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            IN ARRIVO
          </div>
        )}
      </div>
    </motion.div>
  );
};

const GameHub = ({ user, onLogout }) => {
  return (
    <div style={{
      minHeight: '100dvh',
      background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 45%, #24243e 100%)',
      position: 'relative', overflowY: 'auto',
    }}>
      {/* Background blobs */}
      <div style={{ position:'fixed', inset:0, pointerEvents:'none', zIndex:0 }}>
        <motion.div animate={{ scale:[1,1.15,1], opacity:[0.4,0.7,0.4] }} transition={{ duration:6, repeat:Infinity }}
          style={{ position:'absolute', top:'5%', left:'10%', width:500, height:500, borderRadius:'50%', background:'radial-gradient(circle,rgba(139,92,246,0.25),transparent 70%)', filter:'blur(50px)' }} />
        <motion.div animate={{ scale:[1.1,1,1.1], opacity:[0.3,0.6,0.3] }} transition={{ duration:8, repeat:Infinity, delay:2 }}
          style={{ position:'absolute', bottom:'5%', right:'10%', width:400, height:400, borderRadius:'50%', background:'radial-gradient(circle,rgba(236,72,153,0.2),transparent 70%)', filter:'blur(50px)' }} />
        <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(255,255,255,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.02) 1px,transparent 1px)', backgroundSize:'60px 60px' }} />
      </div>

      {/* Floating icons */}
      {[
        { icon:'🏰', x:'8%',  y:'12%', size:36, delay:0    },
        { icon:'🃏', x:'88%', y:'8%',  size:30, delay:0.4  },
        { icon:'🎯', x:'6%',  y:'75%', size:32, delay:0.8  },
        { icon:'🎲', x:'91%', y:'70%', size:28, delay:0.2  },
        { icon:'🏆', x:'15%', y:'45%', size:24, delay:1.1  },
        { icon:'🔮', x:'82%', y:'42%', size:28, delay:0.6  },
        { icon:'🌟', x:'50%', y:'5%',  size:22, delay:1.4  },
        { icon:'🎮', x:'75%', y:'88%', size:20, delay:0.9  },
        { icon:'👑', x:'22%', y:'90%', size:22, delay:1.6  },
        { icon:'🛡', x:'55%', y:'92%', size:24, delay:0.3  },
      ].map((g, i) => (
        <motion.div key={i}
          style={{ position:'fixed', left:g.x, top:g.y, fontSize:g.size, pointerEvents:'none', userSelect:'none', zIndex:0 }}
          animate={{ y:[0,-12,0], opacity:[0.15,0.4,0.15], rotate:[0,5,-5,0] }}
          transition={{ duration:4+i*0.4, delay:g.delay, repeat:Infinity, ease:'easeInOut' }}
        >
          {g.icon}
        </motion.div>
      ))}

      {/* Header */}
      <div style={{ position:'relative', zIndex:1, textAlign:'center', padding:'36px 20px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
        <motion.div initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.6 }}>
          <motion.div
            animate={{ rotate:[0,5,-5,0] }}
            transition={{ duration:4, repeat:Infinity, ease:'easeInOut' }}
            style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:52, height:52, borderRadius:14, background:'linear-gradient(135deg,#7c3aed,#ec4899)', boxShadow:'0 8px 24px rgba(124,58,237,0.5)', marginBottom:12, fontSize:26, fontWeight:900, color:'#fff' }}
          >
            S
          </motion.div>
          <h1 style={{
            fontFamily:"'Cinzel',serif",
            fontSize:'clamp(22px,5vw,38px)', fontWeight:900, margin:'0 0 6px',
            background:'linear-gradient(to right,#a78bfa,#f9a8d4,#60a5fa)',
            WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
            letterSpacing:'0.08em',
          }}>
            SURVIVAL GAMES
          </h1>
          <p style={{ margin:0, fontSize:13, color:'rgba(255,255,255,0.35)', letterSpacing:'0.1em', textTransform:'uppercase', fontWeight:600 }}>
            Scegli il tuo gioco
          </p>
        </motion.div>

        <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.3 }}
          style={{ position:'absolute', top:16, right:16, display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:13, color:'rgba(167,139,250,0.8)', fontWeight:700 }}>
            {user?.username}
          </span>
          <button onClick={onLogout} style={{
            background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)',
            borderRadius:8, padding:'4px 10px', fontSize:11, color:'rgba(255,255,255,0.4)',
            cursor:'pointer', fontWeight:600,
          }}>
            Esci
          </button>
        </motion.div>
      </div>

      {/* Game grid */}
      <div style={{
        position:'relative', zIndex:1,
        maxWidth:900, margin:'0 auto',
        padding:'28px 16px 40px',
        display:'grid',
        gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',
        gap:20,
      }}>
        {GAMES.map((game, i) => (
          <GameCard key={game.id} game={game} index={i} />
        ))}
      </div>
    </div>
  );
};

export default GameHub;